import type { User } from '~/types/user-schema';

/**
 * Middleware to check if the user has access to the page
 * @param {Route} to
 */
export default defineNuxtRouteMiddleware(async (to) => {
    const { session, loggedIn } = useUserSession();

    const isExpired = isSessionExpired(session);

    if (!loggedIn.value || isExpired) {
        return;
    }

    const user = unref(session).user as User | null;
    // Check if the user is trying to access an admin page
    if (!user?.frontendManagementAccess) {
        return;
    }

    const { product, team } = getSsrUrlParams(to.fullPath);

    const lastUsedProduct = product ?? user.lastUsedProduct;
    const lastUsedTeam = team ?? user.lastUsedTeam ?? user.mainTeamUuid ?? null;

    // Stores
    const storeProducts = useProductStore();
    const licenseStore = useLicenseStore();

    const { accessToken } = session.value;

    const loadAdminData = async () => {
        // load the teams store
        const teamsStore = useTeamsStore();
        await teamsStore.loadTeams(accessToken, lastUsedTeam);

        // load the products and license stores
        // We always pass a token which equals force reloading.
        if (!storeProducts.initialized) {
            await storeProducts.loadProducts(accessToken ?? false);
        }

        await licenseStore.loadLicenses();

        storeProducts.setActive(lastUsedProduct);
    };

    const [err, _] = await catchError(loadAdminData());
    if (err) {
        return abortNavigation({
            statusCode: 401,
            statusMessage: 'Unauthorized',
        });
    }
});
